import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCreateContact, useUpdateContact } from '@/hooks/useContacts';
import type { Contact } from '@/types/api.types';

const schema = z.object({
  phone: z
    .string()
    .min(1, 'Telefone obrigatório')
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => {
      const digits = v.startsWith('55') && (v.length === 12 || v.length === 13) ? v.slice(2) : v;
      return digits.length >= 10 && digits.length <= 11;
    }, 'Informe DDD + número  (ex: 54981250935)'),
  name: z.string().optional(),
  documento: z.string().optional(),
  razaoSocial: z.string().optional(),
  nomeFantasia: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
});

type Values = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: Contact | null;
}

export function ContactFormDialog({ open, onOpenChange, contact }: Props) {
  const isEdit = !!contact;
  const { mutate: create, isPending: creating } = useCreateContact();
  const { mutate: update, isPending: updating } = useUpdateContact();
  const isPending = creating || updating;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      phone: '',
      name: '',
      documento: '',
      razaoSocial: '',
      nomeFantasia: '',
      email: '',
    },
  });

  useEffect(() => {
    if (open) {
      const m = contact?.metadata ?? {};
      form.reset({
        phone: contact?.phone ?? '',
        name: contact?.name ?? '',
        documento: (m.documento as string) ?? '',
        razaoSocial: (m.razaoSocial as string) ?? '',
        nomeFantasia: (m.nomeFantasia as string) ?? '',
        email: (m.email as string) ?? '',
      });
    }
  }, [open, contact, form]);

  function onSubmit(values: Values) {
    const name = values.name?.trim() || undefined;
    const metadata: Record<string, unknown> = {};
    if (values.documento?.trim()) metadata.documento = values.documento.trim();
    if (values.razaoSocial?.trim()) metadata.razaoSocial = values.razaoSocial.trim();
    if (values.nomeFantasia?.trim()) metadata.nomeFantasia = values.nomeFantasia.trim();
    if (values.email?.trim()) metadata.email = values.email.trim();

    if (isEdit && contact) {
      update(
        { id: contact.id, data: { phone: values.phone, name: name ?? null, metadata } },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create({ phone: values.phone, name, metadata }, { onSuccess: () => onOpenChange(false) });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar contato' : 'Novo contato'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input placeholder="(54) 98125-0935" {...field} />
                  </FormControl>
                  <FormDescription>DDD + número (sem +55). Ex: 54981250935</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome (opcional)</FormLabel>
                  <FormControl>
                    <Input placeholder="João Silva" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="documento"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Documento</FormLabel>
                    <FormControl>
                      <Input placeholder="00.000.000/0001-00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="contato@empresa.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="razaoSocial"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão Social</FormLabel>
                  <FormControl>
                    <Input placeholder="Empresa Ltda" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nomeFantasia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Fantasia</FormLabel>
                  <FormControl>
                    <Input placeholder="Marca da empresa" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
